
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface IncidentReportViewProps {
  teamId: string;
}

export const IncidentReportView = ({ teamId }: IncidentReportViewProps) => {
  const { t } = useLanguage();
  const [fullScreen, setFullScreen] = useState(false);
  const [reportLink, setReportLink] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (teamId) {
      fetchReportLink();
    }
  }, [teamId]);

  const fetchReportLink = async () => {
    try {
      setIsLoading(true);
      
      // Try to get the link from Supabase
      const { data, error } = await supabase
        .from('team_incident_links' as any)
        .select('link_url')
        .eq('team_id', teamId)
        .maybeSingle();
      
      if (error) throw error;
      
      if (data) {
        setReportLink((data as any).link_url);
        // Also update localStorage for backward compatibility
        localStorage.setItem(`incident-report-link-${teamId}`, (data as any).link_url);
      } else {
        // Fallback to localStorage
        const savedLink = localStorage.getItem(`incident-report-link-${teamId}`);
        if (savedLink) {
          setReportLink(savedLink);
          
          // If found in localStorage but not in database, migrate it
          await supabase
            .from('team_incident_links' as any)
            .insert({
              team_id: teamId,
              link_url: savedLink,
            });
        }
      }
    } catch (error) {
      console.error('Error fetching incident report link:', error);
      
      // Still try localStorage as fallback
      const savedLink = localStorage.getItem(`incident-report-link-${teamId}`) || '';
      setReportLink(savedLink);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFullScreen = () => {
    setFullScreen(!fullScreen);
  };

  if (isLoading) {
    return <div className="p-4 text-center">Loading incident report form...</div>;
  }

  if (!reportLink) {
    return <div className="p-4 text-center">{t("noReportLinkConfigured") || "No incident report link configured."}</div>;
  }

  // If we're in fullscreen mode, use the Sheet component
  if (fullScreen) {
    return (
      <Sheet open={fullScreen} onOpenChange={() => setFullScreen(false)}>
        <SheetContent side="bottom" className="p-0 h-screen w-screen max-w-full sm:max-w-full">
          <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 p-2 border-b">
            <div className="text-sm font-medium flex-1">
              {t("incidentReportForm") || "Incident Report Form"}
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={toggleFullScreen}>
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="w-full h-[calc(100%-40px)]">
            <iframe
              src={reportLink}
              className="w-full h-full border-0"
              title="Incident Report Form"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Standard embedded view
  return (
    <div className="border rounded-lg overflow-hidden h-full">
      <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 p-2 border-b">
        <div className="text-sm font-medium flex-1">
          {t("incidentReportForm") || "Incident Report Form"}
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={toggleFullScreen}>
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="w-full h-[calc(100%-40px)]">
        <iframe
          src={reportLink}
          className="w-full h-full border-0"
          title="Incident Report Form"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
        />
      </div>
    </div>
  );
};
